import React from 'react';

import {assert} from 'Asserts';
import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {ImportSettings} from 'bulk-import/SpreadsheetBulkImportStore';
import {getRegistrySupportedOligoAwareTypes, Registry} from 'constants/RegistryConstants';
import HelpButton from 'help/HelpButton';
import * as RegistryUtil from 'registry/RegistryUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';

const helpPopoverContent = (): JSX.Element => (
  <div>
    <p>
      Schemas let you define new entity types. You can edit existing schemas and add new ones in your registry
      settings.
    </p>
  </div>
);

export interface EntitySchemaSelectionProps {
  label: string;
  registry: Registry | null;
  selectedSchemaId: string | null;
  importSettingsKey?: keyof ImportSettings;
  onRequestClose: () => void;
  tagSchemaFilterFn?: (schema: {id: string}) => boolean;
  shouldDefaultIfSingleItem?: boolean;
}

export default function EntitySchemaSelection(props: EntitySchemaSelectionProps): JSX.Element {
  const handleTagSchemaChange = (tagSchema: {id: string} | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned null for the tagSchema');
    const importSettingsKey = props.importSettingsKey || 'selectedTagSchemaId';
    SpreadsheetBulkImportActions.setImportSetting({[importSettingsKey]: tagSchema.id});
  };

  const openNewSchemaPage = (): void => {
    props.onRequestClose();
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getCreateTagSchemaUrl(props.registry!.s_owner.url_identifier, props.registry!.id)
    );
  };

  return (
    <>
      <FieldLabel label={props.label} labelStyle={LabelStyle.LABEL} isRequired={true} />
      <HelpButton
        renderContent={helpPopoverContent}
        iconClassName="bulkImportModal-helpIcon u-tiny-spacing-left"
      />
      <TagSchemaSelector
        oligoAwareFolderItemTypes={getRegistrySupportedOligoAwareTypes()}
        requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
        onChange={handleTagSchemaChange}
        registryId={props.registry && props.registry.id}
        selectedTagSchemaId={props.selectedSchemaId}
        tagSchemaFilterFn={props.tagSchemaFilterFn}
        shouldDefaultIfSingleItem={props.shouldDefaultIfSingleItem}
      />
      {props.registry &&
        RegistryUtil.isNavStoreRegistry(props.registry) &&
        RegistryUtil.hasSettingsAccess(props.registry) && (
          <div
            className="u-tiny-spacing-top"
            {...getTestProps({component: 'EntitySchemaSelection', key: 'createNewSchemaLink'})}
          >
            Don't see the schema you're looking for? <a onClick={openNewSchemaPage}>Create a new one</a>.
          </div>
        )}
    </>
  );
}
